import React, { useEffect, useId, useRef, useState } from 'react';
import Select from '../shared/Select';
import LibraryDialog from '../Library/LibraryDialog';
import { assessmentNameClashes } from '../../../models/AssessmentsModel';
import AssessmentFiles from './AssessmentFiles';
import NoScenarios from './NoScenarios';
import { readAudioDuration } from '../../../utils/audioDuration';
import '../Library/AddDocumentDialog.css';

const Required = () => (
  <>
    <span className="resui-field-required" aria-hidden="true">*</span>
    <span className="library-sr-only">required</span>
  </>
);

function nameError(clashes, files) {
  if (!clashes.length) return '';
  return clashes.length === 1 && files <= 1
    ? 'An assessment is already called that. Choose a different name.'
    : `Numbering this name would repeat ${clashes.join(', ')}, already in use. Choose a different name.`;
}

/**
 * Add assessment (PRD-1703, handoff "Assessments", stories 12 to 20): a name,
 * a scenario and at least one recording or transcript. One assessment per
 * file; several take the name numbered in selection order. The dialog closes
 * on save and the transfers carry on in the background.
 *
 * A name already in use is refused: the error shows when you leave the field,
 * and Save is held until it changes. A file name already on an assessment only
 * warns, and needs the tick before Save. The form, files included, survives
 * being dismissed. With no usable scenario the dialog says so and links to
 * Settings > Assessments.
 */
function AddAssessmentDialog({ vm, opener, fallbackFocus, onSave, onClose, onManageScenarios }) {
  const ids = useId();
  const nameRef = useRef(null);
  const [nameFocused, setNameFocused] = useState(false);
  const [dupConfirmed, setDupConfirmed] = useState(false);
  const { draft, usableScenarios, narrativeName, assessments } = vm;

  const options = usableScenarios
    .map((s) => ({ value: s.id, label: narrativeName(s) ? `${s.name} (${narrativeName(s)})` : s.name }));
  // Until one is chosen, the first scenario, as the prototype's select opens on it.
  const scenarioId = options.some((o) => o.value === draft.scenarioId) ? draft.scenarioId : options[0]?.value;

  const accepted = draft.files.filter((e) => !e.reject).length;
  const hasDupes = draft.files.some((e) => !e.reject && e.warn);
  const clashes = assessmentNameClashes(assessments, draft.name, accepted);
  // Validation on blur: while typing a clash only clears; leaving the field shows it.
  const error = nameFocused ? '' : nameError(clashes, accepted);
  const canSave = accepted > 0 && !!draft.name.trim() && (!hasDupes || dupConfirmed) && !clashes.length;
  const name = draft.name.trim();

  // The tick belongs to the warning: once no file warns, a later one asks again.
  useEffect(() => { if (!hasDupes) setDupConfirmed(false); }, [hasDupes]);

  const addFiles = (files) => {
    const added = vm.addFiles(files);
    added.forEach((entry, i) => {
      if (entry.reject || entry.kind !== 'audio') return;
      readAudioDuration(files[i]).then((sec) => vm.setFileDuration(entry.key, sec));
    });
  };

  const save = () => {
    if (!canSave) return;
    onSave({ name: draft.name, scenarioId });
  };

  if (!options.length) {
    return <NoScenarios opener={opener} fallbackFocus={fallbackFocus} onClose={onClose} onAddScenario={() => onManageScenarios({ addScenario: true })} />;
  }

  return (
    <LibraryDialog title="Add assessment" size="wide" initialFocus={nameRef} opener={opener} fallbackFocus={fallbackFocus} onClose={onClose}>
      <div className="resui-field library-form-group">
        <label className="resui-field-label" htmlFor={`${ids}-name`}>Assessment name<Required /></label>
        <input
          ref={nameRef}
          id={`${ids}-name`}
          type="text"
          className={`resui-input${error ? ' resui-input--error' : ''}`}
          maxLength={80}
          placeholder="e.g. Strait Transit - Bravo Watch Run A"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={`${ids}-name-help`}
          value={draft.name}
          onFocus={() => setNameFocused(true)}
          onBlur={() => setNameFocused(false)}
          onChange={(e) => vm.setDraft({ name: e.target.value })}
        />
        <div className="assessment-field-help" id={`${ids}-name-help`}>
          Shown as the title on the Assessments page.
          <div>
            {accepted > 1
              ? `${accepted} files selected, so each takes this name numbered in the order they were selected${name ? ` - ${name} 1, ${name} 2` : ''}.`
              : 'Several files take this name numbered in the order they are selected.'}
          </div>
          {error && <div className="assessment-field-error" role="alert">{error}</div>}
        </div>
      </div>

      <div className="resui-field library-form-group">
        <label className="resui-field-label" htmlFor={`${ids}-scenario`}>Scenario<Required /></label>
        <Select
          id={`${ids}-scenario`}
          className="resui-select-field--block"
          value={scenarioId}
          describedBy={`${ids}-scenario-help`}
          options={options}
          onChange={(next) => vm.setDraft({ scenarioId: next })}
        />
        <div className="assessment-field-help" id={`${ids}-scenario-help`}>
          Carries the reference key, SOP and narrative saved on that scenario.{' '}
          <button type="button" className="resui-link" onClick={() => onManageScenarios({ addScenario: false })}>Manage scenarios</button>
          <div>Each option is the scenario name, with its narrative in parentheses.</div>
        </div>
      </div>

      <div className="resui-field library-form-group">
        <span className="resui-field-label" id={`${ids}-files`}>Audio or transcript<Required /></span>
        <AssessmentFiles files={draft.files} labelId={`${ids}-files`} onFiles={addFiles} onRemove={vm.removeFile} />
        {hasDupes && (
          <label className="resui-checkbox assessment-dup-confirm">
            <input type="checkbox" className="resui-checkbox-input" checked={dupConfirmed} onChange={(e) => setDupConfirmed(e.target.checked)} />
            <span className="resui-checkbox-mark" aria-hidden="true" />
            <span className="resui-checkbox-label">Upload anyway, as a separate assessment</span>
          </label>
        )}
      </div>

      <div className="library-dialog-actions">
        <button type="button" className="resui-btn resui-btn--secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="resui-btn resui-btn--primary" disabled={!canSave} onClick={save}>
          {accepted > 1 ? 'Save assessments' : 'Save assessment'}
        </button>
      </div>
    </LibraryDialog>
  );
}

export default AddAssessmentDialog;
